// src/utils/r2Url.js
// FAZ 2 / Görev 1 — R2 CDN URL normalizasyonu (paylaşılabilir util).
//
// Sorun: V3 klipler `v3_r2Url` olarak R2 "dev" host'u (pub-XXXX.r2.dev)
// taşıyabilir. Bu host kalıcı/güvenilir değil; doğrudan <img>/<video> src'ine
// basıldığında kırık görsel üretir. clipsRepo.js içinde aynı mantık var ama
// orada `transformR2Url` export edilmiyor ve dosya KİLİTLİ — bu yüzden
// mantık burada bağımsız bir util olarak tekrar üretildi (tek kaynak: SavedGrid
// ve gelecekteki tüketiciler buradan import eder).
//
// Davranış (clipsRepo.transformR2Url ile birebir):
//   - null/boş/non-string → null (fallback zinciri tetiklensin)
//   - r2.dev host DEĞİLSE → URL olduğu gibi döner
//   - r2.dev host VE rewrite konfigüratı (REACT_APP_R2_PUBLIC_REWRITE) varsa
//     → host yeniden yazılır (kalıcı public CDN)
//   - r2.dev host VE rewrite YOKSA → null (broken host; fallback'e düş)
//
// P3-4 (2026-06): Cloudflare Image Resizing variant URL builder eklendi.
//   buildImageVariant(url, opts)    — tek URL döner
//   buildImageSrcSet(url, widths)   — srcset attribute string'i
//   imageVariantsEnabled()          — env var set mi?
// REACT_APP_CF_IMAGE_BASE set değilse builder'lar orijinal URL döner
// (graceful fallback — kullanıcı CF subscription almadan deploy güvenli).

const _R2_DEV_HOST_RE = /^https?:\/\/pub-[a-f0-9]+\.r2\.dev/i;

function readR2PublicRewrite() {
  try {
    if (typeof process === "undefined" || !process.env) return "";
    const v = process.env.REACT_APP_R2_PUBLIC_REWRITE;
    if (typeof v !== "string" || !v) return "";
    return v.replace(/\/$/, "");
  } catch (_) {
    return "";
  }
}

/**
 * R2 dev host URL'sini kalıcı public CDN'e çevirir; çevrilemiyorsa null döner.
 * @param {*} url Ham medya URL'si.
 * @returns {string|null}
 */
// Üç imzada da `unknown`: gövdeler `typeof url !== "string"` ile SÜZÜYOR.
export function transformR2Url(url: unknown): string | null {
  if (url == null || url === "") return null;
  if (typeof url !== "string") return null;
  if (!url.trim()) return null;
  // 🐞 BUGGIFY · i-medya-cdn-olu — CDN ADRESİNİ ULAŞILAMAZ YAP.
  // Taklit edilen GERÇEK arıza CLAUDE.md'de bir ROLLBACK TETİKLEYİCİSİ olarak
  // yazılı: *"HLS bozuldu (siyah/CORS) → `REACT_APP_R2_PUBLIC_REWRITE`ı yorum
  // satırı yap"*. Yani yanlış/ölü bir medya rewrite'ı üretimde YAŞANMIŞ bir
  // sınıftır. SINANAN ONARIM: kırık görsel zinciri — `imageFallback` markalı
  // placeholder'a çevirmeli (`data-img-fb="1"` izi) ve ekranda "yırtık resim"
  // glifi KALMAMALI. ⚙️ Üretimde derleme zamanı elenir.
  if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-medya-cdn-olu")) {
    return "https://vw-buggify-olu-cdn.invalid/" + String(url).split("/").pop();
  }
  if (!_R2_DEV_HOST_RE.test(url)) return url;
  const rewrite = readR2PublicRewrite();
  if (rewrite) {
    return url.replace(_R2_DEV_HOST_RE, rewrite);
  }
  // Broken host, rewrite yok → fallback zinciri tetiklensin.
  return null;
}

// ─── P3-4: Cloudflare Image Resizing helpers ─────────────────────────────

function readCfImageBase() {
  try {
    if (typeof process === "undefined" || !process.env) return "";
    const v = process.env.REACT_APP_CF_IMAGE_BASE;
    if (typeof v !== "string" || !v) return "";
    return v.replace(/\/$/, "");
  } catch (_) {
    return "";
  }
}

/**
 * P3-4 — CF Image Transformations aktif mi? .env'de
 * REACT_APP_CF_IMAGE_BASE set ise true.
 *
 * @returns {boolean}
 */
export function imageVariantsEnabled() {
  return readCfImageBase().length > 0;
}

/**
 * P3-4 — Tek bir variant URL üretir.
 *
 * Pattern (Cloudflare Image Transformations URL syntax):
 *   {base}/{params}/{originUrl}
 * Örnek:
 *   https://cdn.mylasa.app/cdn-cgi/image/width=320,format=auto,fit=cover/https://pub-xxx.r2.dev/avatar.jpg
 *
 * CF base set DEĞİLSE veya origin URL kullanılamıyorsa (null/boş)
 * orijinal URL aynen döner — bozulma yok, sadece optimizasyon kapalı.
 *
 * @param {string|null|undefined} url Origin görsel URL'i
 * @param {object} opts { width?, height?, fit?, format?, quality? }
 * @returns {string|null}
 */
export function buildImageVariant(url: unknown, opts: any = {}): string | null {
  // ⚠️ DAVRANIŞ FARKI (kasıtlı, kusur düzeltmesi): eski satır
  // `if (!url || typeof url !== "string") return url || null;` idi — yani
  // string OLMAYAN ama truthy bir değer (sayı, nesne) geldiğinde onu OLDUĞU
  // GİBİ geri veriyordu. O değer doğrudan `<img src>`e gidiyor → kırık
  // görsel. Dosyanın kendi JSDoc'u zaten `@returns {string|null}` diyor;
  // tip yazılınca sözleşmenin ihlal edildiği görüldü (dönüş `{}` çıkıyordu).
  // Artık string olmayan her şey `null` — çağıranların hepsi zaten
  // `|| effectiveOrigin` / `|| null` yedeğiyle okuyor.
  if (typeof url !== "string" || !url) return null;
  // R2 dev host varsa önce normalize et — broken host CF'ye gitmesin.
  const normalized = transformR2Url(url);
  if (!normalized) return null;
  const base = readCfImageBase();
  // 🐞 BUGGIFY · i-gorsel-varyant-kapali — GÖRSEL DÖNÜŞÜMÜNÜ KAPAT.
  // Taklit edilen GERÇEK arıza: Cloudflare Image Transformations zone
  // ayarından kapanır / `REACT_APP_CF_IMAGE_BASE` deploy'da düşer. O anda
  // ürün küçültülmüş varyant yerine TAM BOY orijinali istemek zorunda kalır.
  // SINANAN ONARIM: `if (!base) return normalized` yedeği — görsel yine
  // yüklenmeli, `srcset` boş kalmalı ve düzen BOZULMAMALI (kırık kart yok).
  // ⚙️ Üretimde derleme zamanı elenir (`false && …` → Terser bloğu siler).
  if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-gorsel-varyant-kapali")) return normalized;
  if (!base) return normalized;

  const params = [];
  if (Number.isFinite(opts.width) && opts.width > 0) {
    params.push(`width=${Math.round(opts.width)}`);
  }
  if (Number.isFinite(opts.height) && opts.height > 0) {
    params.push(`height=${Math.round(opts.height)}`);
  }
  params.push(`fit=${opts.fit || "cover"}`);
  params.push(`format=${opts.format || "auto"}`);
  if (Number.isFinite(opts.quality) && opts.quality > 0 && opts.quality <= 100) {
    params.push(`quality=${Math.round(opts.quality)}`);
  }

  return `${base}/${params.join(",")}/${normalized}`;
}

/**
 * P3-4 — `<img srcset>` attribute string'i üretir.
 *
 * Örnek dönüş:
 *   "https://cdn.../w=160/origin 160w, https://cdn.../w=320/origin 320w, ..."
 *
 * CF base set değilse boş string döner — caller `srcset` attribute'unu
 * vermez (browser orijinal `src` ile devam eder).
 *
 * @param {string} url Origin görsel URL'i
 * @param {number[]} widths Hedef genişlikler (px)
 * @param {object} [opts] Ortak transform opts (fit/format/quality)
 * @returns {string}
 */
// ⚠️ `widths = []` idi → TS `never[]` sayıyor ve `number[]` geçen tek
// çağıranı (`ResponsiveImage:68`) REDDEDİYORDU. Kusur çağıranda değil
// BURADAYDI — boş koleksiyon literali tiplenir (oturumun `useState([])`
// sınıfının varsayılan-parametre biçimi).
export function buildImageSrcSet(url: unknown, widths: number[] = [], opts: object = {}) {
  if (!imageVariantsEnabled()) return "";
  if (!url || typeof url !== "string") return "";
  if (!Array.isArray(widths) || widths.length === 0) return "";
  const parts = [];
  for (const w of widths) {
    if (!Number.isFinite(w) || w <= 0) continue;
    const variant = buildImageVariant(url, { ...opts, width: w });
    if (variant) parts.push(`${variant} ${Math.round(w)}w`);
  }
  return parts.join(", ");
}

export default transformR2Url;
